import React, { useState } from "react";
import { motion } from "framer-motion";
import { Activity, Flame, Zap, Info, X, Flag } from "lucide-react";
import { fmtDuration } from "@/lib/format";

function parseLocal(iso) {
  const [y, m, d] = (iso || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

// Segmented dial: the outer ring is painted as 3 colour-coded arcs that
// visualise the DAY'S FUEL COMPOSITION (Base + Carb Load + Training/Race
// Fuel) at true proportion. A thinner bright inner arc grows as the
// athlete eats, so a glance shows "what am I fuelling today, and how much
// of it have I banked?". Beta feedback Feb 27 2026: "we need a better
// system, love the dial, so can we show extra calories on that, the base
// on that". Replaces the older dual-nested ring which hid race-day peaks.
function SegmentedRing({ segments, eaten, size = 220, stroke = 18, fillStroke = 10, children }) {
  const total = Math.max(1, segments.reduce((s, x) => s + Math.max(0, x.kcal), 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const eatenPct = Math.min(1, Math.max(0, eaten / total));
  const fillLen = c * eatenPct;
  const over = eaten > total * 1.05;
  const eatenColor = over ? "#FF3B30" : "#FFFFFF";
  // Compute stroke-dasharray + offset for each segment (butt caps so
  // segments stack cleanly without overlap; small visual gap of `gap` px).
  const gap = 4;
  let acc = 0;
  const arcs = segments.filter((s) => s.kcal > 0).map((seg) => {
    const startFrac = acc / total;
    acc += seg.kcal;
    const endFrac = acc / total;
    const segLen = Math.max(0, (endFrac - startFrac) * c - gap);
    const dashOffset = -(startFrac * c) + (gap / 2);
    return { color: seg.color, dash: `${segLen} ${c}`, off: dashOffset };
  });
  return (
    <div className="relative shrink-0" style={{ width: size, height: size, filter: "drop-shadow(0 0 24px rgba(167,139,250,0.22))" }}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Faint underlay so empty segments still read as a full ring */}
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} />
        {/* Composition arcs — Base / Carb Load / Fuel */}
        {arcs.map((a, i) => (
          <motion.circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={a.color} strokeWidth={stroke} strokeLinecap="butt"
            strokeDasharray={a.dash} strokeDashoffset={a.off}
            initial={{ opacity: 0 }} animate={{ opacity: 0.85 }}
            transition={{ duration: 0.8, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }} />
        ))}
        {/* Inner "eaten so far" fill — thin bright track inside the composition */}
        <motion.circle cx={size / 2} cy={size / 2} r={r - stroke / 2 - fillStroke / 2 - 3}
          fill="none" stroke={eatenColor} strokeWidth={fillStroke} strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c - fillLen }}
          transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
    </div>
  );
}

function Macro({ label, value, target, color, delay = 0 }) {
  const pct = target ? Math.round((value / target) * 100) : 0;
  const over = target && value > target;
  return (
    <motion.div data-testid={`hero-macro-${label.toLowerCase()}`}
      initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-200">
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color, boxShadow: `0 0 8px ${color}88` }} /> {label}
        </span>
        <span className="text-xs font-mono text-white">
          <span className="text-white font-bold">{Math.round(value)}</span>
          <span className="text-zinc-500">{target ? ` / ${Math.round(target)}g` : "g"}</span>
          {target ? <span className="ml-2 font-bold" style={{ color: over ? "#FF3B30" : color }}>{pct}%</span> : null}
        </span>
      </div>
      <div className="h-2 bg-white/[0.06] overflow-hidden">
        <motion.div className="h-full" style={{ background: over ? "#FF3B30" : color }}
          initial={{ width: 0 }} animate={{ width: `${Math.min(100, pct)}%` }}
          transition={{ duration: 0.9, delay: delay + 0.1, ease: [0.22, 1, 0.36, 1] }} />
      </div>
    </motion.div>
  );
}

export default function TodayHero({ date, consumed = {}, target, readiness = null, trainMin = 0, baseTarget = 0, earnedFuelKcal = 0, plannedFuelKcal = 0, burnedKcal = 0, hasTrainingToday = false, plannedTrainMin = 0, isRace = false, carbLoadBumpGkg = 0, daysUntilRace = null, carbTarget = 0, carbLoadKcal = 0, sessionsDone = 0, sessionsTotal = 0 }) {
  // Small "i" info popover explaining the burn-vs-target maths (beta
  // feedback Feb 26 2026 — don't like the wordy explainer, prefer an
  // info icon that reveals on tap).
  const [showFuelInfo, setShowFuelInfo] = useState(false);
  const d = parseLocal(date);
  const isToday = date === new Date().toISOString().slice(0, 10);
  const dateLabel = d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

  const plannedTotalKcal = Math.max(0, Math.round(target?.energy || 0));
  const eaten = Math.round(consumed.kcal || 0);
  const rColor = readiness == null ? "#71717a" : readiness >= 80 ? "#34C759" : readiness >= 60 ? "#E2E30A" : "#FF3B30";

  // Prefer completed (history) minutes when we have them, otherwise fall
  // back to *planned* minutes from the plan or TrainingPeaks so the strap
  // never says "REST DAY" while a real workout is queued on the calendar
  // below (beta feedback Feb 26 2026: TP-synced sessions on a plan-generated
  // rest day showed a contradictory "REST DAY" fuel line).
  // Use the LARGER of logged vs planned/TP minutes so the strap can never
  // contradict the session list below it (user report June 2026: strap said
  // "40m training" while the day listed a 1h13m run + a 35m walk).
  const effectiveTrainMin = Math.max(trainMin || 0, plannedTrainMin || 0);
  const showTraining = hasTrainingToday || effectiveTrainMin > 0;
  const isCarbLoadDay = !isRace && carbLoadBumpGkg > 0 && daysUntilRace != null && daysUntilRace > 0;
  // Segment kcal for the composition ring. Base = anything that isn't
  // carb-load bump or training fuel (i.e. metabolic maintenance + any
  // goal adjustment); Carb Load = weight × g/kg × 4 kcal; Fuel = planned
  // training/race supported kcal.
  const baseSegKcal = Math.max(0, plannedTotalKcal - (carbLoadKcal || 0) - (plannedFuelKcal || 0));
  // Training/race fuel is only counted in the headline target once it's been
  // EARNED — i.e. the session is marked done or synced as completed (user
  // request, June 2026). Unearned fuel stays visible as a locked line so the
  // athlete knows what completing the session unlocks.
  const unearnedFuel = Math.max(0, (plannedFuelKcal || 0) - (earnedFuelKcal || 0));
  const earnedFuelSeg = Math.max(0, Math.min(earnedFuelKcal || 0, plannedFuelKcal || 0));
  const totalKcal = Math.max(0, Math.round(plannedTotalKcal - unearnedFuel));
  // No plan covers this day, so there is no target. Showing "kcal to go"
  // against a zero target reads as a lie — show what's been logged instead.
  const noTarget = totalKcal <= 0;
  const kcalLeft = totalKcal - eaten;
  const over = totalKcal && eaten > totalKcal * 1.05;
  const kcalStatus = noTarget ? { t: "No plan for this week", c: "#71717A" }
    : eaten < totalKcal * 0.9 ? { t: `${Math.abs(kcalLeft)} kcal to go`, c: "#4C9AFF" }
    : eaten > totalKcal * 1.1 ? { t: `${Math.abs(kcalLeft)} kcal over`, c: "#FF3B30" }
    : { t: "On target", c: "#34C759" };
  // Palette DELIBERATELY distinct from the macro palette below (carbs
  // green / protein blue / fat orange) — beta feedback Feb 27 2026:
  // athletes assumed the ring was macros. Cool violet + hot magenta +
  // rose read as a fuel-composition story, not a macro story.
  const ringSegments = [
    { key: "base", label: "Base", kcal: baseSegKcal, color: "#A78BFA" },
    { key: "carbLoad", label: "Carb Load", kcal: Math.max(0, carbLoadKcal || 0), color: "#EC4899" },
    { key: "fuel", label: isRace ? "Race Fuel Earned" : "Training Fuel Earned", kcal: earnedFuelSeg, color: isRace ? "#FF3B30" : "#FB7185" },
  ];
  // Day-type ribbon: single unambiguous label that tells the athlete
  // WHAT KIND of day this is before they scan the numbers. Beta Feb 27
  // 2026 idea (d): drop the ambiguity of "training today" vs "rest day"
  // on race-week taper days.
  // Softer per-type text colours so the ribbon reads clearly without
  // "shouting" (beta feedback Feb 27 2026: the pure red PEAK FUEL banner
  // was too bright). Left border + icon stay saturated for context; the
  // label sits in a muted variant.
  const dayType = isRace
    ? { label: "Race Day · Peak Fuel", color: "#FF3B30", softColor: "#FCA5A5", Icon: Flag }
    : isCarbLoadDay
      ? { label: `T-${daysUntilRace} · Carb Load`, color: "#E2E30A", softColor: "#FEF08A", Icon: Zap }
      : showTraining
        ? { label: `${fmtDuration(effectiveTrainMin)} Training · Fuel Day`, color: "#4C9AFF", softColor: "#BFDBFE", Icon: Activity }
        : { label: "Rest Day · Recovery", color: "#71717A", softColor: "#A1A1AA", Icon: Flame };
  const fuelLine = isRace
    ? (target ? `Race day — carb-fuel with ~${Math.round(target.carb)}g carbs.` : "Race day — top up carbs before the gun.")
    : isCarbLoadDay
      ? (target ? `Race-week carb load · T-${daysUntilRace} · ~${Math.round(target.carb)}g carbs (+${carbLoadBumpGkg}g/kg).` : `Race-week carb load · T-${daysUntilRace} · +${carbLoadBumpGkg}g/kg.`)
      : showTraining
        ? (target ? `${fmtDuration(effectiveTrainMin)} training today — fuel with ~${Math.round(target.carb)}g carbs.` : `${fmtDuration(effectiveTrainMin)} training today — keep carbs topped up.`)
        : "Rest day — prioritise protein and recovery.";

  return (
    <motion.section data-testid="today-hero"
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden border border-white/10 bg-gradient-to-br from-[#141414] via-[#111111] to-[#0A0A0A]">
      {/* Layered atmospheric glow — signature "sport hero" backdrop */}
      <div className="pointer-events-none absolute -top-40 -right-32 h-80 w-80 rounded-full bg-[#E2E30A]/12 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-32 h-80 w-80 rounded-full bg-[#4C9AFF]/10 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

      <div className="relative p-5 sm:p-7">
        {/* Header — date + fuel-line pill. Readiness gauge removed from here
            (beta feedback Feb 26 2026) so the ring is the only focal point;
            readiness lives inside the Morning Report card. */}
        <div className="mb-6 sm:mb-7">
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.05 }}>
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#E2E30A]" style={{ boxShadow: "0 0 10px #E2E30A" }} />
              <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#E2E30A]">{isToday ? "Today" : "Selected day"}</p>
            </div>
            <p className="font-display text-2xl sm:text-3xl uppercase tracking-tight text-white leading-none mt-2">{dateLabel}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] ${
                isCarbLoadDay
                  ? "border-2 border-[#E2E30A] bg-[#E2E30A]/[0.12] text-[#E2E30A] font-black"
                  : "border border-white/10 bg-black/40 text-zinc-300"}`}
                style={isCarbLoadDay ? { boxShadow: "0 0 16px rgba(226,227,10,0.35)" } : undefined}>
                {isRace ? <Flag className="h-3 w-3 text-[#FF3B30]" /> : isCarbLoadDay ? <Zap className="h-3 w-3" /> : showTraining ? <Activity className="h-3 w-3 text-[#4C9AFF]" /> : <Flame className="h-3 w-3 text-[#FF9500]" />}
                <span>{fuelLine}</span>
              </div>
              {isRace && (
                <span data-testid="hero-race-pill"
                  className="inline-flex items-center gap-1 border border-[#FF3B30]/60 bg-[#FF3B30]/[0.08] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#FF3B30]">
                  <Flag className="h-3 w-3" /> Race Day
                </span>
              )}
            </div>
          </motion.div>
        </div>

        {/* Central hero: segmented composition ring + macros */}
        <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-7 sm:gap-9 items-center">
          <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center gap-3 mx-auto">
            <SegmentedRing segments={ringSegments} eaten={noTarget ? 0 : eaten} size={220} stroke={18} fillStroke={10}>
              <span className="text-[9px] font-bold uppercase tracking-[0.28em] text-zinc-400 mb-1">
                {noTarget ? "kcal logged" : over ? "kcal over" : "kcal to go"}
              </span>
              <span className="font-display text-5xl sm:text-6xl text-white leading-none tabular-nums" data-testid="hero-kcal-left">{noTarget ? eaten : Math.abs(kcalLeft)}</span>
              {totalKcal ? (
                <span className="text-[11px] font-mono text-zinc-400 mt-2 tabular-nums">
                  <span className="text-white font-bold">{eaten}</span>
                  <span className="text-zinc-600"> / </span>
                  <span data-testid="hero-total-kcal">{totalKcal}</span>
                </span>
              ) : null}
            </SegmentedRing>
            {kcalStatus && (
              <motion.span data-testid="hero-status"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.4 }}
                className="text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 border-2"
                style={{ color: kcalStatus.c, borderColor: kcalStatus.c, backgroundColor: `${kcalStatus.c}11` }}>
                {kcalStatus.t}
              </motion.span>
            )}
            {/* Composition legend — one row per non-zero segment. Reads
                like a breakdown card so the athlete sees at a glance
                what makes up their total. */}
            <div className="grid grid-cols-1 gap-1.5 w-full max-w-[260px]" data-testid="hero-composition">
              {ringSegments.filter((s) => s.kcal > 0).map((seg) => (
                <div key={seg.key} className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-zinc-300">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 shrink-0" style={{ background: seg.color, boxShadow: `0 0 6px ${seg.color}66` }} />
                    <span>{seg.label}</span>
                  </span>
                  <span className="text-white font-bold tabular-nums">{seg.kcal} kcal</span>
                </div>
              ))}
              {unearnedFuel > 0 && (
                <div data-testid="hero-fuel-locked"
                  className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 shrink-0 border border-dashed border-zinc-600" />
                    <span>{isRace ? "Race fuel locked" : "Training fuel locked"}</span>
                  </span>
                  <span className="tabular-nums">+{unearnedFuel} kcal</span>
                </div>
              )}
              <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider border-t border-white/10 pt-1.5 mt-1">
                <span className="text-[#E2E30A] font-black">Total</span>
                <span className="text-white font-black tabular-nums" data-testid="hero-total-legend">{noTarget ? "No target yet" : `${totalKcal} kcal`}</span>
              </div>
              {noTarget && (
                <p className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 text-center mt-0.5" data-testid="hero-no-plan-hint">
                  Build a plan for this week to get calorie &amp; macro targets
                </p>
              )}
              {unearnedFuel > 0 && (
                <p className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 text-center mt-0.5" data-testid="hero-fuel-unlock-hint">
                  {sessionsDone > 0 && sessionsTotal > 1
                    ? `${sessionsDone} of ${sessionsTotal} sessions done — the rest is worth +${unearnedFuel} kcal`
                    : `Complete today's session to unlock up to +${unearnedFuel} kcal`}
                </p>
              )}
              <button type="button" data-testid="hero-fuel-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowFuelInfo(true); }}
                aria-label="Explain how the daily target works"
                className="mt-1 flex items-center justify-center gap-1 text-[9px] font-mono uppercase tracking-wider text-zinc-400 hover:text-[#E2E30A] transition-colors">
                <Info className="h-3 w-3" /> How this works
              </button>
            </div>
            {/* On-demand explainer popover. Adapts to state so athletes get
                the right story pre-training (fuel to unlock), post-training
                (fuel earned + burn already baked in) and on rest days
                (no training top-up today). Widened Feb 26 2026 — was only
                showing when a session was already done. */}
            {showFuelInfo && (
              <div data-testid="hero-fuel-explain"
                className="mt-2 relative w-full max-w-[320px] border border-[#E2E30A]/40 bg-[#141414] p-3 shadow-lg text-left">
                <button type="button" onClick={() => setShowFuelInfo(false)}
                  aria-label="Close" data-testid="hero-fuel-info-close"
                  className="absolute top-1.5 right-1.5 p-1 text-zinc-400 hover:text-white">
                  <X className="h-3 w-3" />
                </button>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] font-bold pr-4">How your daily target works</p>

                {/* Race-week carb-load explainer — always shown first when
                    the day is inside the T-3 / T-2 / T-1 window, whether or
                    not the athlete has training queued. Beta feedback Feb
                    27 2026: "include carb loading in how your daily target
                    works". */}
                {isCarbLoadDay && (
                  <div className="mt-2 border border-[#E2E30A]/40 bg-[#E2E30A]/[0.06] p-2.5" data-testid="hero-fuel-explain-carbload">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#E2E30A] font-bold flex items-center gap-1">
                      <Zap className="h-3 w-3" /> Race-week carb load · T-{daysUntilRace}
                    </p>
                    <p className="text-[11px] text-zinc-100 leading-relaxed mt-1">
                      {daysUntilRace === 1
                        ? "Night before your race — top up muscle glycogen. "
                        : daysUntilRace === 2
                          ? "Two days out — start topping up carbs while training tapers. "
                          : "Three days out — small carb bump starts the load. "}
                      Carbs are bumped by <span className="text-[#E2E30A] font-bold">+{carbLoadBumpGkg} g/kg</span>{target?.carb ? <> — that's <span className="text-white font-bold">~{Math.round(target.carb)}g total</span> today</> : null}. Protein and fat stay steady, hydration goes up.
                    </p>
                  </div>
                )}

                {/* Line 1 — the maths, adapted to the day type. */}
                {noTarget ? (
                  <p className="text-[11px] text-zinc-100 leading-relaxed mt-1.5">
                    There's no plan covering this day, so there's no calorie or macro target yet — the dial is just showing what you've logged. Build a plan for this week and your base calories, training fuel and macros all light up here.
                  </p>
                ) : !plannedFuelKcal ? (
                  <p className="text-[11px] text-zinc-100 leading-relaxed mt-1.5">
                    {isCarbLoadDay
                      ? <>Your target is your <span className="text-[#4C9AFF] font-bold">Base {baseTarget} kcal</span> plus the carb-load bump above — no training top-up to unlock, just eat the extra carbs.</>
                      : <>Rest day — your target is your <span className="text-[#4C9AFF] font-bold">Base {baseTarget} kcal</span>. Eat toward that; nothing extra to unlock today.</>}
                  </p>
                ) : burnedKcal > 0 && earnedFuelKcal > 0 ? (
                  <p className="text-[11px] text-zinc-100 leading-relaxed mt-1.5">
                    Completed training burnt <span className="text-[#FF6A3D] font-bold">~{Math.round(burnedKcal)} kcal</span> — that's earned <span className="text-[#E2E30A] font-bold">+{Math.round(earnedFuelKcal)} kcal</span> of fuel on top of your <span className="text-[#4C9AFF] font-bold">Base {baseTarget}</span>. Fuel is credited from the work actually done, not from how many sessions are ticked.
                  </p>
                ) : earnedFuelKcal > 0 ? (
                  <p className="text-[11px] text-zinc-100 leading-relaxed mt-1.5">
                    Sessions done — <span className="text-[#E2E30A] font-bold">+{Math.round(earnedFuelKcal)} kcal</span> fuel unlocked on top of your <span className="text-[#4C9AFF] font-bold">Base {baseTarget}</span>.
                  </p>
                ) : (
                  <p className="text-[11px] text-zinc-100 leading-relaxed mt-1.5">
                    You have <span className="text-[#E2E30A] font-bold">+{plannedFuelKcal} kcal</span> fuel to unlock — complete a session today (planned or a bonus workout logged in TrainingPeaks) and it credits to your <span className="text-[#4C9AFF] font-bold">Base {baseTarget}</span> automatically.
                  </p>
                )}

                {/* Line 2 — the reassurance. Kept short. */}
                {(plannedFuelKcal || 0) > 0 && (
                  <p className="text-[10px] text-zinc-400 leading-relaxed mt-2">
                    We don't subtract your burn from what you eat — it's already baked into the fuel top-up. Just eat toward the daily target.
                  </p>
                )}
              </div>
            )}
          </motion.div>

          <div className="space-y-4">
            <Macro label="Carbs" value={consumed.carb || 0} target={target?.carb} color="#34C759" delay={0.25} />
            <Macro label="Protein" value={consumed.protein || 0} target={target?.protein} color="#4C9AFF" delay={0.35} />
            <Macro label="Fat" value={consumed.fat || 0} target={target?.fat} color="#FF9500" delay={0.45} />
            {/* Yellow "unlock" banner removed Feb 26 2026 per beta
                feedback ("just don't think it's needed or the correct
                wording"). The ring itself + the "i" popover already
                communicate remaining fuel and how it credits — the
                extra sentence duplicated the message and confused
                athletes who'd done bonus workouts. */}
          </div>
        </div>
      </div>
    </motion.section>
  );
}
